/**
 * ExamCountdown - small widget on Dashboard showing days-to-exam.
 * If user hasn't set exam_date, prompts them to complete onboarding.
 */
import { useAuth } from "@/context/AuthContext";
import { Calendar, AlertCircle } from "lucide-react";

const ExamCountdown = ({ onComplete, hidePrompt = false }) => {
  const { user } = useAuth();
  if (!user) return null;

  if (!user.exam_date) {
    if (hidePrompt) return null;
    return (
      <button
        data-testid="exam-countdown-prompt"
        onClick={onComplete}
        className="w-full text-left p-4 rounded-2xl glass border border-dashed border-border hover:border-primary/40 transition flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-sm">Tell us your exam date</p>
          <p className="text-xs text-muted-foreground">Personalise your study plan in 30 seconds.</p>
        </div>
      </button>
    );
  }

  const exam = new Date(user.exam_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ms = exam - today;
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  const formatted = exam.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

  let tone = "primary";
  let message = "Keep up the pace.";
  if (days === 0) { tone = "warning"; message = "Exam day - best of luck!"; }
  else if (days <= 14) { tone = "warning"; message = "Final stretch - focus on weak areas."; }
  else if (days <= 30) { tone = "warning"; message = "1 month to go - daily practice matters now."; }
  else if (days <= 60) { message = "Solid runway - build the habit."; }

  const toneClass = tone === "warning"
    ? "bg-warning/15 text-warning"
    : "bg-primary/15 text-primary";

  return (
    <div data-testid="exam-countdown" className="p-4 rounded-2xl glass border border-border flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClass}`}>
        <Calendar className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">Your ADC Part 1 exam</p>
        <p className="text-xl font-bold leading-tight">
          <span className="text-2xl">{days}</span> day{days === 1 ? "" : "s"} to go
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{formatted} · {message}</p>
      </div>
    </div>
  );
};

export default ExamCountdown;
